import { useState, useEffect, useRef, useMemo } from "react";

//components
import TextMessage from "./TextMessage";

//tools
import cc from "classcat";

//icons
import PlayIcon from "../../assets/play-icon.svg";
import SpeachLoaderIcon from "../../assets/dual-ring-loader.svg";

//styles
import styles from "./AudioMessage.module.css";

const AudioMessage = ({ message, audioPlayedIdsRef }) => {
	const audioRef = useRef(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [duration, setDuration] = useState(0);
	const [currentTime, setCurrentTime] = useState(0);
	const [revealed, setRevealed] = useState(false);
	const [audioUrl, setAudioUrl] = useState(message.audio_url || null);
	const [speachLoading, setSpeachLoading] = useState(false);
	const timelineRef = useRef(null);
	const dragging = useRef(false);

	const icon = useMemo(() => {
		if (speachLoading) {
			return <img
				src={SpeachLoaderIcon}
				alt='Speach loading icon'
				className={styles["audio-player-play-icon"]}
			/>
		} else if (isPlaying) {
			return <svg
				width='18'
				height='18'
				viewBox='0 0 18 18'>
				<rect
					x='2'
					y='2'
					width='4'
					height='14'
					rx='1'
					fill='#1976d2'
				/>
				<rect
					x='12'
					y='2'
					width='4'
					height='14'
					rx='1'
					fill='#1976d2'
				/>
			</svg>;
		}
		return <img
			src={PlayIcon}
			alt='Play icon'
			className={styles["audio-player-play-icon"]}
		/>;
	}, [isPlaying, speachLoading]);

	const togglePlay = async () => {
		if (!audioUrl && message.id) {
			await requestSpeech();
			return;
		}
		const audio = audioRef.current;
		if (!audio) return;
		if (isPlaying) {
			audio.pause();
		} else {
			audio.play();
		}
		setIsPlaying(!isPlaying);
	};

	const seekTo = (clientX) => {
		const timeline = timelineRef.current;
		const audio = audioRef.current;
		if (!timeline || !audio) return;
		const rect = timeline.getBoundingClientRect();
		const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
		const percent = x / rect.width;
		const newTime = percent * duration;
		audio.currentTime = newTime;
		setCurrentTime(newTime);
	};

	const handleThumbMouseDown = (e) => {
		dragging.current = true;
		document.body.style.userSelect = "none";
	};

	const handleTimelineClick = (e) => {
		seekTo(e.clientX);
	};

	const formatTime = (t) => {
		const sec = Math.max(0, Math.floor(t));
		const min = Math.floor(sec / 60);
		const s = sec % 60;
		return `${min}:${s.toString().padStart(2, "0")}`;
	};

	const timeLeft = duration - currentTime;

	const thumbPosition = (currentTime / (duration || 1)) * 100;

	const requestSpeech = async () => {
		setSpeachLoading(true);
		try {
			const response = await __cng_common.api_service.post("/ai_chat/synthesize_message_speech", {
				message_id: message.id,
			});

			if (!response.success) return;
			setAudioUrl(response.audio_url);

			setTimeout(() => {
				const audio = audioRef.current;
				if (audio) {
					audio.load();
					if (isPlaying) {
						audio.pause();
					} else {
						audio.play();
					}
					setIsPlaying(!isPlaying);
				}
			}, 100);
		} catch (error) {
			console.error("Error requesting speech synthesis:", error);
		} finally {
			setSpeachLoading(false);
		}

	};

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;

		const handleLoadedMetadata = () => {
			setDuration(audio.duration || 0);
			setCurrentTime(audio.currentTime || 0);
		};
		const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
		const handleEnded = () => setIsPlaying(false);

		audio.addEventListener("loadedmetadata", handleLoadedMetadata);
		audio.addEventListener("timeupdate", handleTimeUpdate);
		audio.addEventListener("ended", handleEnded);

		return () => {
			audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
			audio.removeEventListener("timeupdate", handleTimeUpdate);
			audio.removeEventListener("ended", handleEnded);
		};
	}, []);

	useEffect(() => {
		if (
			message?.autoplay &&
			message?.id &&
			!audioPlayedIdsRef.current.includes(message.id)
		) {
			console.log("Autoplaying audio message ID:", message.id);
			togglePlay();
		}
	}, [message?.autoplay, message?.id, audioPlayedIdsRef]);

	useEffect(() => {
		if (message.audio_url) {
			setAudioUrl(message.audio_url);
		}
	}, [message.audio_url]);

	useEffect(() => {
		const handleMouseMove = (e) => {
			if (!dragging.current) return;
			seekTo(e.clientX);
		};
		const handleMouseUp = (e) => {
			if (dragging.current) {
				seekTo(e.clientX);
				dragging.current = false;
				document.body.style.userSelect = "";
			}
		};
		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	});

	return (
		<div className={styles["audio-message-wrap"]}>
			<div className={styles["audio-player-root"]}>
				<button
					type='button'
					onClick={togglePlay}
					className={cc([
						styles["audio-player-play-btn"],
						isPlaying && styles["audio-player-play-btn--active"],
					])}>
					{icon}
				</button>
				<div
					className={styles["audio-player-timeline-wrap"]}
					ref={timelineRef}
					onClick={handleTimelineClick}>
					<div className={styles["audio-player-timeline-bar"]} />
					<div
						className={styles["audio-player-thumb-custom"]}
						style={{ "--thumb-position": `${thumbPosition}%` }}
						onMouseDown={handleThumbMouseDown}
					/>
				</div>
				<div className={styles["audio-player-time"]}>
					{formatTime(timeLeft)}
				</div>
			</div>
			{message.text && (
				<div
					className={cc([
						styles["audio-message-text"],
						revealed && styles["audio-message-text--revealed"],
					])}
					onClick={(e) => {
						if (e?.target?.classList?.contains("product-modal-button")) return;
						setRevealed((v) => !v);
					}}>
					<TextMessage message={message} />
				</div>
			)}
			<audio
				ref={audioRef}
				src={audioUrl}
				className={styles["audio-player-audio"]}
				preload='metadata'
			/>
		</div>
	);
};

export default AudioMessage;

